import { defineStore } from 'pinia';
import SecureLS from 'secure-ls';
import jwt_decode from 'jwt-decode';
import axios from 'axios';
import config from '@/plugins/config';

const ls = new SecureLS({ isCompression: false });

export function getPersistedAuthState() {
	try {
		const raw = ls.get('spark');
		if (raw) {
			const data = (typeof raw === 'object' && raw.auth) ? raw.auth : (typeof raw === 'string' ? JSON.parse(raw) : raw);
			if (data && typeof data === 'object') {
				return data;
			}
		}
	} catch (e) {
		console.warn('[SecureLS Read Error]:', e);
	}
	return {};
}

export function saveAuthState(state) {
	try {
		const plain = JSON.parse(JSON.stringify(state));
		ls.set('spark', plain);
	} catch (e) {
		console.warn('[SecureLS Save Error]:', e);
	}
}

const defaultState = () => {
	const base = {
		accessToken: null,
		isAuthenticated: false,
		tokenExpiry: null,
		jwtPayload: null,
		isSuperAdmin: false,
		isConsultant: false,
		isClient: false,
		isAdmin: false,
		isGeneralUser: false,
		isOrgAdmin: false,
		isSparkProductEnabled: false,
		hasOrgSettingsAccess: false,
		token: null,
		loginLoop: 0,
		isTokenExpired: false,
		redirectLink: null,
		currentUser: null,
		organisationsList: null,
		currentOrganisation: {},
		tiny_mce_api_key: '',
		usersWithVertexRoles: [],
		lastActive: null,
		isRefreshing: false,
	};
	const saved = getPersistedAuthState();
	return {
		...base,
		...saved,
	};
};

export const useAuthStore = defineStore('auth', {
	state: () => defaultState(),

	getters: {
		canSendProposal: (state) => {
			return state.isSuperAdmin && !state.isAdmin && !state.isConsultant;
		},
		canExportLeads: (state) => {
			return !state.isConsultant;
		},
		isGlobalSuperAdmin: (state) => {
			return state.isSuperAdmin && !state.isAdmin && !state.isConsultant;
		},
		canEditContentLibrary: (state) => {
			return (state.isSuperAdmin || state.isAdmin) && !state.isConsultant;
		},
		organisationId: (state) => state.currentUser?.organisation?.id || state.currentOrganisation?.id || 0,
	},

	actions: {
		resetState() {
			try {
				ls.remove('spark');
			} catch (e) {
				// ignore storage removal errors
			}
			const base = {
				accessToken: null,
				isAuthenticated: false,
				tokenExpiry: null,
				jwtPayload: null,
				isSuperAdmin: false,
				isConsultant: false,
				isClient: false,
				isAdmin: false,
				isGeneralUser: false,
				isOrgAdmin: false,
				isSparkProductEnabled: false,
				hasOrgSettingsAccess: false,
				token: null,
				loginLoop: 0,
				isTokenExpired: false,
				redirectLink: null,
				currentUser: null,
				organisationsList: null,
				currentOrganisation: {},
				tiny_mce_api_key: '',
				usersWithVertexRoles: [],
				lastActive: null,
				isRefreshing: false,
			};
			Object.assign(this, base);
		},

		clearLocalSession() {
			this.resetState();
			try {
				localStorage.removeItem('user');
				localStorage.removeItem('access_token');
				localStorage.removeItem('spark');
				sessionStorage.removeItem('user');
				sessionStorage.removeItem('access_token');
				sessionStorage.removeItem('spark');
			} catch (e) {
				// ignore storage removal errors
			}
		},

		redirectToAuthSettings() {
			this.clearLocalSession();

			const authBaseUrl = (config?.getAuthBaseUrl?.() || process.env.VUE_APP_AUTH_BASE_URL || '').replace(/\/+$/, '');
			if (!authBaseUrl) {
				window.location.href = config.getLoginLink();
				return;
			}

			window.location.href = `${authBaseUrl}/user-settings/`;
		},

		setAccessToken(accessToken) {
			this.accessToken = accessToken;
			this.isAuthenticated = true;
			this.isTokenExpired = false;
			saveAuthState(this.$state);
		},

		setRefreshToken(refreshToken) {
			this.token = refreshToken;
			saveAuthState(this.$state);
		},

		setRefreshing(isRefreshing) {
			this.isRefreshing = isRefreshing;
		},

		setJwtPayload(payload) {
			this.jwtPayload = payload;
			this.tokenExpiry = payload ? (payload.expires_at || (payload.exp ? payload.exp * 1000 : null)) : null;
			this.lastActive = new Date().getTime();
			this.isTokenExpired = false;

			if (!payload) {
				saveAuthState(this.$state);
				return;
			}

			const extractRoles = (rawRoles) => {
				if (!rawRoles) return [];
				if (typeof rawRoles === 'string') return [rawRoles.toLowerCase().trim()];
				if (Array.isArray(rawRoles)) {
					return rawRoles
						.map(r => (typeof r === 'string' ? r : (r && r.name ? r.name : (r && r.role ? r.role : ''))))
						.filter(Boolean)
						.map(r => r.toLowerCase().trim());
				}
				return [];
			};

			const activeOrgUuid = payload.organisation_uuid;
			const globalRoles = [
				...extractRoles(payload.roles),
				...extractRoles(payload.role),
				...extractRoles(payload.vertex_roles),
			];

			const isSuperRole = (r) => r.includes('super') || r === 'super_admin' || r === 'superadmin' || r === 'super-admin';
			const isOrgAdminRole = (r) => r === 'org_admin' || r === 'organisation_admin' || r === 'organization_admin';
			const isSparkAdminRole = (r) =>
				r === 'spark_admin' || r === 'spark admin' || r === 'spark-admin' ||
				r === 'admin_support' || r === 'admin support' || r === 'admin-support';
			const isProductAdminRole = (r) => r === 'admin' || r === 'administrator';
			const isSparkConsultantRole = (r) =>
				r === 'spark_consultant' || r === 'spark consultant' || r === 'spark-consultant' || r === 'consultant';

			let isSuper = globalRoles.some(isSuperRole);
			let isSparkAdmin = false;
			let isSparkConsultant = false;
			let isOrgAdmin = globalRoles.some(isOrgAdminRole);
			let hasSparkProduct = false;

			const hasOrgRoles = Array.isArray(payload.organisation_role) && payload.organisation_role.length > 0;
			const hasActiveOrgFilter = Boolean(activeOrgUuid || payload.organisation_id);

			let matchedOrgRole = null;
			// Evaluate organisation-scoped admin and products/roles
			if (hasOrgRoles) {
				for (const orgRole of payload.organisation_role) {
					const orgUuid = orgRole.organisation_uuid || orgRole.orguuid || orgRole.org_uuid;
					const isTargetOrg = !hasActiveOrgFilter ||
						(Boolean(activeOrgUuid) && orgUuid === activeOrgUuid) ||
						(Boolean(payload.organisation_id) && orgRole.organisation_id === payload.organisation_id);
					if (!isTargetOrg) continue;

					if (!matchedOrgRole) {
						matchedOrgRole = orgRole;
					}

					if (orgRole.is_organisation_admin) {
						isOrgAdmin = true;
					}
					if (orgRole.products && Array.isArray(orgRole.products)) {
						for (const product of orgRole.products) {
							const prodName = (product.product || product.application || product.name || '').toLowerCase().trim();
							if (prodName.includes('spark')) {
								hasSparkProduct = true;
								// Pseudo-check Org Admin: Auth dynamically mints product admin claims into
								// orgRole.products when is_organisation_admin is true (including on Vertex).
								// Only trust product-level roles when the user is NOT an Org Admin.
								if (!orgRole.is_organisation_admin && !isOrgAdmin) {
									const productRoles = extractRoles(product.roles || product.role);
									if (productRoles.some(isSuperRole)) {
										isSuper = true;
									}
									if (productRoles.some(isSparkAdminRole)) {
										isSparkAdmin = true;
									}
									if (productRoles.some(isSparkConsultantRole)) {
										isSparkConsultant = true;
									}
								}
							}
						}
					}
				}
				if (!matchedOrgRole) {
					matchedOrgRole = payload.organisation_role[0];
				}
			}

			const hasGlobalOrgAdmin = globalRoles.some(isOrgAdminRole);
			if (globalRoles.some(isSparkAdminRole)) isSparkAdmin = true;
			if (globalRoles.some(isSparkConsultantRole)) isSparkConsultant = true;
			if (!hasOrgRoles && !hasGlobalOrgAdmin && globalRoles.some(isProductAdminRole)) isSparkAdmin = true;

			const isSparkProductEnabled = Boolean(
				isSuper || isSparkAdmin || isSparkConsultant || hasSparkProduct
			);
			this.isSparkProductEnabled = isSparkProductEnabled;

			// Role hierarchy: Super Admin > Admin > Consultant
			if (isSuper) {
				this.isSuperAdmin = true;
				this.isAdmin = false;
				this.isConsultant = false;
			} else if (isSparkAdmin) {
				this.isSuperAdmin = false;
				this.isAdmin = true;
				this.isConsultant = false;
			} else if (isSparkConsultant) {
				this.isSuperAdmin = false;
				this.isAdmin = false;
				this.isConsultant = true;
			} else {
				this.isSuperAdmin = false;
				this.isAdmin = false;
				this.isConsultant = false;
			}

			this.isOrgAdmin = isOrgAdmin;

			if (this.isSuperAdmin) {
				this.hasOrgSettingsAccess = true;
			} else if (this.isAdmin && this.isOrgAdmin) {
				this.hasOrgSettingsAccess = true;
			} else {
				this.hasOrgSettingsAccess = false;
			}

			this.isGeneralUser = !this.isSuperAdmin && !this.isAdmin && !this.isConsultant;

			this.currentUser = {
				id: payload.user_id,
				uuid: payload.user_uuid,
				first_name: payload.first_name,
				email: payload.email,
				last_name: payload.last_name,
				organisation: {
					id: matchedOrgRole ? matchedOrgRole.organisation_id : (payload.organisation_id || 0),
					orguuid: payload.organisation_uuid || (matchedOrgRole ? (matchedOrgRole.organisation_uuid || matchedOrgRole.orguuid || matchedOrgRole.org_uuid) : ''),
					name: matchedOrgRole ? (matchedOrgRole.organisation || matchedOrgRole.name || '') : '',
				},
			};

			if (!this.currentOrganisation || !this.currentOrganisation.id) {
				this.currentOrganisation = {
					id: this.currentUser.organisation.id,
					orguuid: this.currentUser.organisation.orguuid,
					name: this.currentUser.organisation.name,
				};
			}

			saveAuthState(this.$state);
		},

		authenticated(authenticated) {
			this.isAuthenticated = !!authenticated;
			if (authenticated) {
				this.isTokenExpired = false;
			}
			saveAuthState(this.$state);
		},

		setTinyMCEAPIKey(tiny_mce_api_key) {
			this.tiny_mce_api_key = tiny_mce_api_key;
			saveAuthState(this.$state);
		},

		setIsTokenExpired() {
			this.isTokenExpired = true;
			saveAuthState(this.$state);
		},

		setRedirectLink(link) {
			this.redirectLink = link;
			saveAuthState(this.$state);
		},

		setOrganisationsList(orgs) {
			this.organisationsList = orgs;
			saveAuthState(this.$state);
		},

		setCurrentOrganisation(org) {
			this.currentOrganisation = org;
			saveAuthState(this.$state);
		},

		setCurrentUser(user) {
			this.currentUser = user;
			saveAuthState(this.$state);
		},

		setUsersWithVertexRoles(users) {
			this.usersWithVertexRoles = users || [];
			saveAuthState(this.$state);
		},

		updateLastActive() {
			this.lastActive = new Date().getTime();
			saveAuthState(this.$state);
		},

		async refreshToken() {
			if (this.isRefreshing || !this.accessToken) return;
			this.setRefreshing(true);
			try {
				const apiBase = (process.env.VUE_APP_API_BASE_URL || '') + '/auth/token/refresh';
				const response = await axios.post(apiBase, {
					access_token: this.accessToken,
				});
				if (response.data && response.data.status && response.data.user) {
					const newAccessToken = response.data.user.access_token;
					const decodedToken = jwt_decode(newAccessToken);
					this.setJwtPayload(decodedToken);
					if ((this.isOrgAdmin && !this.isSparkProductEnabled && !this.isSuperAdmin) ||
						(this.isGeneralUser || (!this.isSuperAdmin && !this.isAdmin && !this.isConsultant))) {
						this.redirectToAuthSettings();
						return false;
					}
					this.setAccessToken(newAccessToken);
				}
			} catch (error) {
				console.error('Token refresh failed:', error);
			} finally {
				this.setRefreshing(false);
			}
		},

		redirectToLogin() {
			window.location.href = config.getLoginLink();
		},

		logout(payload) {
			const clearAuthSession = typeof payload === 'object' && payload !== null
				? (payload.clearAuthSession !== undefined ? !!payload.clearAuthSession : true)
				: (payload !== undefined ? !!payload : true);
			let logoutUrl = `${config.getLoginLink()}`;

			if (clearAuthSession || this.isTokenExpired || (!this.isSuperAdmin && !this.isAdmin && !this.isConsultant)) {
				logoutUrl = `${logoutUrl}&logout=true`;
			}

			if (this.isTokenExpired) {
				logoutUrl = `${logoutUrl}&error=TOKEN_EXPIRE`;
			} else if (!this.isSuperAdmin && !this.isAdmin && !this.isConsultant) {
				logoutUrl = `${logoutUrl}&message=Unauthorized access. Please contact administrator.`;
			}

			this.resetState();

			window.location.href = logoutUrl;
		},
	},

	persist: {
		key: 'spark',
	},
});

export default useAuthStore;
